import { type } from "arktype";

import { OPERATING_EXPENSE_DESTINATION, type ExpenseCategoryId } from "@/domain/operating-expenses";

export type SupplierKind = "MILL" | "TRADING" | "CARRIER" | "OTHER";

export const supplyCategoryIdSchema = type.enumerated(
	"sugar",
	"packaging",
	"inbound_freight",
	"insurance",
	"maintenance",
	"software_services",
	"forklift_gas",
);

export const SUPPLY_CATEGORIES = [
	{ id: "sugar", name: "Açúcar" },
	{ id: "packaging", name: "Embalagens" },
	{ id: "inbound_freight", name: "Frete de entrada" },
	{ id: "insurance", name: "Seguros" },
	{ id: "maintenance", name: "Manutenção" },
	{ id: "software_services", name: "Software e serviços digitais" },
	{ id: "forklift_gas", name: "Gás de empilhadeira" },
] as const;

export type SupplyCategoryId = typeof supplyCategoryIdSchema.infer;

export const SUGAR_SUPPLIERS = [
	{
		id: "bevap",
		name: "Bevap",
		legalName: "Bioenergética Vale do Paracatu S.A.",
		taxId: "08793343000162",
		kind: "MILL" as const,
		categoryId: "sugar" as const,
		bankAliases: ["BIOENERGETICA VALE DO PARACATU SA"],
	},
	{
		id: "panorama",
		name: "Panorama",
		legalName: "Usina Panorama S.A.",
		taxId: "08704527000109",
		kind: "MILL" as const,
		categoryId: "sugar" as const,
		bankAliases: ["USINA PANORAMA S/A"],
	},
	{
		id: "coruripe",
		name: "Coruripe — Campo Florido",
		legalName: "S.A. Usina Coruripe Açúcar e Álcool — filial Campo Florido",
		taxId: "12229415001435",
		kind: "MILL" as const,
		categoryId: "sugar" as const,
		bankAliases: ["S A USINA CORURIPE ACUCAR E ALCOOL"],
	},
	{
		id: "copa",
		name: "Copa Empacotadora",
		legalName: "Copa Empacotadora de Alimentos Ltda.",
		taxId: "14408359000107",
		kind: "OTHER" as const,
		categoryId: "sugar" as const,
		bankAliases: ["COPA EMPACOTADORA DE ALIMENTOS LTDA"],
	},
	{
		id: "cerradao",
		name: "Cerradão",
		legalName: "Cooperativa dos Produtores de Cana-de-Açúcar, Açúcar e Álcool — filial Cerradão",
		taxId: "61149589005229",
		kind: "MILL" as const,
		categoryId: "sugar" as const,
		bankAliases: [],
	},
	{
		id: "cambui",
		name: "Cambuí",
		legalName: "Cambuí Açúcar e Álcool Ltda.",
		taxId: "09022388000104",
		kind: "MILL" as const,
		categoryId: "sugar" as const,
		bankAliases: [],
	},
	{
		id: "arapora",
		name: "Araporã",
		legalName: "Araporã Bioenergia S.A. em Recuperação Judicial",
		taxId: "19818301000155",
		kind: "MILL" as const,
		categoryId: "sugar" as const,
		bankAliases: [],
	},
	{
		id: "jatiboca",
		name: "Jatiboca",
		legalName: null,
		taxId: null,
		kind: "MILL" as const,
		categoryId: "sugar" as const,
		bankAliases: [],
	},
	{
		id: "cacu",
		name: "Caçu",
		legalName: null,
		taxId: null,
		kind: "MILL" as const,
		categoryId: "sugar" as const,
		bankAliases: [],
	},
	{
		id: "copersucar",
		name: "Copersucar",
		legalName: null,
		taxId: null,
		kind: "TRADING" as const,
		categoryId: "sugar" as const,
		bankAliases: [],
	},
];

export const INBOUND_FREIGHT_SUPPLIERS = [
	{
		id: "mv-transportes",
		name: "MV Transportes e Logística",
		legalName: "MV Transportes e Logística Ltda.",
		taxId: "60568282000150",
		kind: "CARRIER" as const,
		categoryId: "inbound_freight" as const,
		bankAliases: ["MV TRANSPORTES E LOGISTICA LTDA"],
	},
	{
		id: "holding-agro",
		name: "Holding Agro",
		legalName: "Transportadora Holding Agro Ltda.",
		taxId: "61831474000170",
		kind: "CARRIER" as const,
		categoryId: "inbound_freight" as const,
		bankAliases: ["TRANSPORTADORA HOLDING AGRO - LTDA"],
	},
	{
		id: "rodovita",
		name: "Rodovita Transportes",
		legalName: "Rodovita Transportes Ltda.",
		taxId: "47061706000127",
		kind: "CARRIER" as const,
		categoryId: "inbound_freight" as const,
		bankAliases: ["RODOVITA TRANSPORTES LTDA"],
	},
	{
		id: "cootraavesp",
		name: "Cootraavesp",
		legalName: "Cooperativa dos Transportadores Autônomos de Vespasiano",
		taxId: "04034826000122",
		kind: "CARRIER" as const,
		categoryId: "inbound_freight" as const,
		bankAliases: ["COOPERATIVA DOS TRANSPORTADORES AUTONOMOS DE VESPASIANO - CO"],
	},
	{
		id: "2m-transportes",
		name: "2M Transportes Rodoviários",
		legalName: "2M Transportes Rodoviários Ltda.",
		taxId: "00433707000191",
		kind: "CARRIER" as const,
		categoryId: "inbound_freight" as const,
		bankAliases: ["2M TRANSPORTES RODOVIARIOS LTDA"],
	},
	{
		id: "transpanorama",
		name: "Transpanorama Transportes",
		legalName: "Transpanorama Transportes S.A.",
		taxId: "01937440000132",
		kind: "CARRIER" as const,
		categoryId: "inbound_freight" as const,
		bankAliases: ["TRANSPANORAMA TRANSPORTES S.A."],
	},
	{
		id: "d-albieri",
		name: "D. Albieri Transporte e Logística",
		legalName: "D. Albieri Transporte e Logística Ltda.",
		taxId: "03059726000198",
		kind: "CARRIER" as const,
		categoryId: "inbound_freight" as const,
		bankAliases: ["D. ALBIERI TRANSPORTE E LOGISTICA LTDA"],
	},
	{
		id: "sete-lagoas-transportes",
		name: "Sete Lagoas Transportes",
		legalName: "Sete Lagoas Transportes Ltda.",
		taxId: "03098929000193",
		kind: "CARRIER" as const,
		categoryId: "inbound_freight" as const,
		bankAliases: ["SETE LAGOAS TRANSPORTES LTDA"],
	},
];

export const PACKAGING_SUPPLIERS = [
	{
		id: "patena",
		name: "Patena",
		legalName: "Patena Indústria e Comércio de Resinas e Filmes Plásticos Ltda.",
		taxId: "08869805000188",
		kind: "OTHER" as const,
		categoryId: "packaging" as const,
		bankAliases: ["PATENA INDUSTRIA E COMERCIO DE RESINAS E FILMES PLASTICOS LTDA"],
	},
];

export const OPERATING_EXPENSE_SUPPLIERS = [
	{
		id: "porto-seguro",
		name: "Porto Seguro",
		legalName: "Porto Seguro Companhia de Seguros Gerais",
		taxId: "61198164000160",
		kind: "OTHER" as const,
		categoryId: "insurance" as const,
		bankAliases: ["PORTO SEGURO COMPANHIA DE SEGUROS GERAIS"],
	},
	{
		id: "mecantec",
		name: "Mecantec",
		legalName: "Mecantec Manutenção e Serviços Ltda.",
		taxId: "37158402000129",
		kind: "OTHER" as const,
		categoryId: "maintenance" as const,
		bankAliases: ["MECANTEC MANUTENCAO E SERVICOS LTDA"],
	},
	{
		id: "arquivei",
		name: "Arquivei",
		legalName: "Arquivei Serviços Online Ltda.",
		taxId: "19427033000140",
		kind: "OTHER" as const,
		categoryId: "software_services" as const,
		bankAliases: ["ARQUIVEI SERVICOS ON LINE LTDA"],
	},
	{
		id: "pluggy",
		name: "Pluggy",
		legalName: "Pluggy Brasil Instituição de Pagamento Ltda.",
		taxId: "37943755000130",
		kind: "OTHER" as const,
		categoryId: "software_services" as const,
		bankAliases: ["PLUGGY BRASIL INSTITUICAO DE PAGAMENTO LTDA"],
	},
	{
		id: "nacional-gas",
		name: "Nacional Gás",
		legalName: "Nacional Gás Butano Distribuidora Ltda.",
		taxId: "06980064017581",
		kind: "OTHER" as const,
		categoryId: "forklift_gas" as const,
		bankAliases: ["NACIONAL GAS BUTANO DISTRIBUIDORA LTDA"],
	},
];

export const SUPPLIERS = [
	...SUGAR_SUPPLIERS,
	...PACKAGING_SUPPLIERS,
	...INBOUND_FREIGHT_SUPPLIERS,
	...OPERATING_EXPENSE_SUPPLIERS,
];

function operatingExpenseClassification(
	input: { supplierName: string },
	expenseCategoryId: ExpenseCategoryId,
	categoryName: string,
) {
	return {
		explanation: `${categoryName} — ${input.supplierName}`,
		destination: OPERATING_EXPENSE_DESTINATION,
		expenseCategoryId,
	};
}

export const SupplierClassifications = {
	fromCategory(input: { categoryId: string; supplierKind: SupplierKind; supplierName: string }) {
		switch (input.categoryId) {
			case "sugar":
				return {
					explanation:
						input.supplierKind === "MILL"
							? `Pagamento de açúcar à usina ${input.supplierName}`
							: `Pagamento de açúcar ao fornecedor ${input.supplierName}`,
					destination: "Açúcar",
					expenseCategoryId: null,
				};
			case "packaging":
				return {
					explanation: `Pagamento de embalagens ao fornecedor ${input.supplierName}`,
					destination: "Embalagens",
					expenseCategoryId: null,
				};
			case "inbound_freight":
				return {
					explanation: `Frete de entrada do açúcar com ${input.supplierName}`,
					destination: "Frete de entrada",
					expenseCategoryId: null,
				};
			case "insurance":
				return operatingExpenseClassification(input, "insurance", "Seguro");
			case "maintenance":
				return operatingExpenseClassification(input, "maintenance", "Manutenção");
			case "software_services":
				return operatingExpenseClassification(
					input,
					"software_services",
					"Software e serviços digitais",
				);
			case "forklift_gas":
				return operatingExpenseClassification(input, "forklift_gas", "Gás de empilhadeira");
			default:
				throw new Error(`Categoria sem regra de classificação: ${input.categoryId}`);
		}
	},
};
